import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';

/**
 * SaaS Metrics Funnel Events
 *
 * Funnel:
 *   Website Visitors → Signups → Activated → Paying → Retained
 *
 * Events tracked:
 * - user_signed_up: First-time user registration (via OAuth)
 * - user_activated: User connects their first device
 * - subscription_started: User becomes a paying customer (future)
 * - subscription_renewed: User's subscription renews (future)
 * - subscription_cancelled: User cancels subscription (future)
 */

// Event names as constants for type safety
export const AnalyticsEvents = {
  // Signup funnel
  USER_SIGNED_UP: 'user_signed_up',
  USER_ACTIVATED: 'user_activated',

  // Subscription events (for future use)
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',

  // Onboarding
  SURVEY_COMPLETED: 'survey_completed',

  // Feature usage events
  DEVICE_CONNECTED: 'device_connected',
  DEVICE_STREAM_STARTED: 'stream_started',
  DEVICE_STREAM_STOPPED: 'stream_stopped',
} as const;

export type AnalyticsEvent = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// Event property types
interface SignupProperties {
  auth_provider: 'google';
  signup_source?: string;
}

interface ActivationProperties {
  device_id: string;
  device_name?: string;
  days_since_signup?: number;
}

interface SubscriptionProperties {
  plan_id?: string;
  plan_name?: string;
  price?: number;
  currency?: string;
  billing_period?: 'monthly' | 'yearly';
}

interface DeviceConnectedProperties {
  device_id: string;
  device_name?: string;
  is_first_device?: boolean;
}

interface StreamProperties {
  device_id: string;
  source_type?: string;
  destination_type?: string;
}

export interface SurveyProperties {
  use_case: string;
  hardware_readiness: string;
  discovery_source: string;
}

type EventProperties = {
  [AnalyticsEvents.USER_SIGNED_UP]: SignupProperties;
  [AnalyticsEvents.USER_ACTIVATED]: ActivationProperties;
  [AnalyticsEvents.SURVEY_COMPLETED]: SurveyProperties;
  [AnalyticsEvents.SUBSCRIPTION_STARTED]: SubscriptionProperties;
  [AnalyticsEvents.SUBSCRIPTION_RENEWED]: SubscriptionProperties;
  [AnalyticsEvents.SUBSCRIPTION_CANCELLED]: SubscriptionProperties;
  [AnalyticsEvents.DEVICE_CONNECTED]: DeviceConnectedProperties;
  [AnalyticsEvents.DEVICE_STREAM_STARTED]: StreamProperties;
  [AnalyticsEvents.DEVICE_STREAM_STOPPED]: StreamProperties;
};

/**
 * Hook for tracking SaaS metrics and funnel events
 *
 * Usage:
 * ```tsx
 * const { trackSignup, trackActivation, trackDeviceConnected } = useAnalytics();
 *
 * // On first signup
 * trackSignup({ auth_provider: 'google' });
 *
 * // On first device connection (activation)
 * trackActivation({ device_id: '123', device_name: 'My Drone' });
 * ```
 */
export function useAnalytics() {
  const posthog = usePostHog();

  const track = useCallback(
    <E extends AnalyticsEvent>(event: E, properties?: EventProperties[E]) => {
      posthog?.capture(event, properties);
    },
    [posthog],
  );

  // Convenience methods for common events

  const trackSignup = useCallback(
    (properties: SignupProperties) => {
      track(AnalyticsEvents.USER_SIGNED_UP, properties);
      // Also set user property to mark them as signed up
      posthog?.people?.set({ signed_up_at: new Date().toISOString() });
    },
    [track, posthog],
  );

  const trackActivation = useCallback(
    (properties: ActivationProperties) => {
      track(AnalyticsEvents.USER_ACTIVATED, properties);
      // Set user property to mark activation
      posthog?.people?.set({
        activated_at: new Date().toISOString(),
        is_activated: true,
      });
    },
    [track, posthog],
  );

  const trackSurveyCompleted = useCallback(
    (properties: SurveyProperties) => {
      track(AnalyticsEvents.SURVEY_COMPLETED, properties);
      posthog?.people?.set({
        use_case: properties.use_case,
        hardware_readiness: properties.hardware_readiness,
        discovery_source: properties.discovery_source,
        survey_completed_at: new Date().toISOString(),
      });
    },
    [track, posthog],
  );

  const trackDeviceConnected = useCallback(
    (properties: DeviceConnectedProperties) => {
      track(AnalyticsEvents.DEVICE_CONNECTED, properties);
      // Set first device timestamp
      posthog?.people?.set_once({ first_device_connected_at: new Date().toISOString() });
    },
    [track, posthog],
  );

  const trackStreamStarted = useCallback(
    (properties: StreamProperties) => {
      track(AnalyticsEvents.DEVICE_STREAM_STARTED, properties);
      posthog?.people?.set_once({ first_stream_at: new Date().toISOString() });
    },
    [track, posthog],
  );

  const trackStreamStopped = useCallback(
    (properties: StreamProperties) => {
      track(AnalyticsEvents.DEVICE_STREAM_STOPPED, properties);
    },
    [track],
  );

  // Subscription events (for future use)
  const trackSubscriptionStarted = useCallback(
    (properties: SubscriptionProperties) => {
      track(AnalyticsEvents.SUBSCRIPTION_STARTED, properties);
      posthog?.people?.set({
        subscribed_at: new Date().toISOString(),
        is_paying: true,
        plan_id: properties.plan_id,
        plan_name: properties.plan_name,
      });
    },
    [track, posthog],
  );

  const trackSubscriptionRenewed = useCallback(
    (properties: SubscriptionProperties) => {
      track(AnalyticsEvents.SUBSCRIPTION_RENEWED, properties);
    },
    [track],
  );

  const trackSubscriptionCancelled = useCallback(
    (properties: SubscriptionProperties) => {
      track(AnalyticsEvents.SUBSCRIPTION_CANCELLED, properties);
      posthog?.people?.set({
        cancelled_at: new Date().toISOString(),
        is_paying: false,
      });
    },
    [track, posthog],
  );

  // Set user properties (for segmentation)
  const setUserProperties = useCallback(
    (properties: Record<string, unknown>) => {
      posthog?.people?.set(properties);
    },
    [posthog],
  );

  return {
    // Core tracking
    track,

    // Funnel events
    trackSignup,
    trackActivation,
    trackSurveyCompleted,

    // Feature events
    trackDeviceConnected,
    trackStreamStarted,
    trackStreamStopped,

    // Subscription events (future)
    trackSubscriptionStarted,
    trackSubscriptionRenewed,
    trackSubscriptionCancelled,

    // Utilities
    setUserProperties,
  };
}
